import apiClient from '../client';

// NOTE: `baseURL` override is a temporary workaround until the apiClient is
// refactored to use `/api` as base and embed the version in the path.
// Same caveat as api/v2/candidates.ts.
const V2_BASE = '/api/v2';

/**
 * A message from `/api/v2/mailbox/messages` — an email still eligible to
 * become a tender (already filtered and de-duplicated server-side), newest
 * first. Keys are snake_case because the `apiClient` response interceptor runs
 * `convertKeysToSnake` on every payload: the backend record component
 * `receivedAt` therefore arrives as `received_at`, `null` when Graph did not
 * return a `receivedDateTime` for the message.
 */
export interface MailboxMessageV2 {
  exchange_message_id: string;
  email: string;
  subject: string;
  received_at: string | null;
}

export interface MailboxFetchPayloadV2 {
  emails: MailboxMessageV2[];
}

/**
 * The v2 response envelope after the interceptor snake-cases keys. The v2
 * endpoints wrap every response — success and error alike — in this shape and
 * set the HTTP status to `status_code`. The interceptor's error branch reads
 * `data.detail`, which this envelope does not carry, so each call below widens
 * `validateStatus` to keep the envelope out of the reject path and reads
 * `error` itself.
 */
interface V2Envelope<T> {
  status_code: number;
  error: string | null;
  payload: T | null;
}

/**
 * Fetch the queue of emails eligible to become tenders. Throws with the
 * envelope's `error` message on a 500.
 */
export async function fetchMailboxMessagesV2(
  params: { top?: number } = {},
): Promise<MailboxMessageV2[]> {
  const response = await apiClient.get<V2Envelope<MailboxFetchPayloadV2>>(
    '/mailbox/messages',
    {
      baseURL: V2_BASE,
      params: { top: params.top ?? 50 },
      // Envelope survives on both the 200 and the service's own 500.
      validateStatus: (status) => status === 200 || status === 500,
    },
  );
  const envelope = response.data;
  if (envelope.status_code !== 200) {
    throw new Error(envelope.error ?? 'Failed to load mailbox messages');
  }
  return envelope.payload?.emails ?? [];
}

/**
 * The three expected outcomes of an import. 409 (already imported) and 422
 * (rejected by the mailbox filter rules) are not failures — they are reported
 * back distinctly so the page can name each rather than collapsing them into a
 * generic error.
 */
export type MailboxImportOutcomeV2 =
  | 'imported'
  | 'already_imported'
  | 'filtered_out';

/**
 * Import one message as a tender via the redesign pipeline. The tender is
 * created `@Async` after the response is written, so no tender id or title
 * comes back — the caller only learns which of the three outcomes occurred.
 *
 * `Cache-Control: no-store` is sent because `importTender` is a mutating GET
 * (recorded as a known defect in the spec §8); the header keeps a proxy or the
 * browser from replaying it.
 */
export async function importMailboxMessageV2(
  messageId: string,
): Promise<MailboxImportOutcomeV2> {
  const response = await apiClient.get<V2Envelope<unknown>>(
    `/mailbox/messages/${messageId}/importTender`,
    {
      baseURL: V2_BASE,
      headers: { 'Cache-Control': 'no-store' },
      validateStatus: (status) =>
        status === 201 || status === 409 || status === 422,
    },
  );
  switch (response.status) {
    case 201:
      return 'imported';
    case 409:
      return 'already_imported';
    default:
      return 'filtered_out';
  }
}
